<template>
  <NuxtLayout>
    <ClientOnly>
      <ModalUnlockPostWithTokens
        v-if="openModalUnlockPost"
        @cancel="openModalUnlockPost = !openModalUnlockPost"
        :model="unlockPostModel"
        :post="unlockPostPost"
      />

      <div id="model_profile" class="container">
        <template v-if="!loadingModel && !errorModel">
          <div class="model-wrapper">
            <h3 v-if="!model?.webstar" class="title">
              {{ model?.name }}
            </h3>

            <div class="video-wrapper"></div>

            <div class="model-video-list">
              <div>
                <div v-show="!errorPost" class="carousel-wrapper">
                  <div class="carousel" ref="carousel">
                    <template v-if="loadingPost">
                      <div class="slide">
                        <MovieBlockedContainer :placeholder="true" />
                      </div>
                    </template>

                    <template v-else-if="!post || !Object.keys(post).length">
                      <p v-if="!post || !Object.keys(post).length">
                        {{ $t("noRecordsFound") }}
                      </p>
                    </template>

                    <template v-else>
                      <div class="slideVertical">
                        <PostSorter :element="post" :model="model" />
                        <hr />
                      </div>
                      <p v-if="!post">{{ $t("noRecords") }}</p>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div v-if="loadingModel" class="loading-model">
            <Spinner></Spinner>
          </div>
          <div v-if="errorModel" class="error-model">
            {{ errorModel }}
            {{ $t("error") }}
          </div>
        </template>
      </div>
    </ClientOnly>
  </NuxtLayout>
</template>

<script setup lang="ts">
import resources from "@/utils/resources";
import config from "~/config";
import process from "@/utils/process";

import { Post, PostData } from "~/types/Post";
import { Model } from "~/types/Model";
import _NuxtApp from "~/types/NuxtApp";

import { UserConfig } from "@/modules/video/UserConfig";

type _Model = Model & { chat: any };

const { $EventBus, $i18n, $ThemeManager } = <_NuxtApp>useNuxtApp();

const $router = useRouter();
const $route = useRoute();

const post: Ref<Post | null> = ref(null);
const model: Ref<_Model | null> = ref(null);

const openModalUnlockPost = ref(false);
const unlockPostModel = ref({});
const unlockPostPost = ref({});

let languageChanged = false;

const loadingChat = ref(false);
const loadingModel = ref(true);
const loadingPost = ref(true);

const errorModel = ref(false);
const errorPost = ref(false);
let moviePlaying: null | {
  ref: string;
  handler: (action: string, value?: any) => void;
} = null;

function getFetchModelData(username: string) {
  if (username[0] == "@") username = username.substring(1);

  return {
    id: username,
    username,
    "include[]": ["subscription_plans"],
    lang: $i18n?.locale?.value || "es",
  };
}

function getPost(postID: string | number, modelID: string | number) {
  postID = postID.toString();

  resources.models
    .getPost({ postID, modelID })
    .then(({ data }) => {
      post.value = data;
      loadingPost.value = false;
    })
    .catch((error) => {
      loadingPost.value = false;
      errorPost.value = error.status !== 404 && error.body.length > 0;
    });
}

function loadPost(username: string, postID: any) {
  loadingModel.value = true;
  loadingPost.value = true;
  errorModel.value = false;

  const fetchModelData = getFetchModelData(username);

  resources.models
    .get(fetchModelData)
    .then(({ data }) => {
      loadingChat.value = true;
      loadingModel.value = false;
      model.value = data as _Model;
      if ((<any>model.value).length === 0) {
        //If the language didn't change, and the model doesnt exist redirect to 404.
        if (languageChanged == false) $router.push({ name: "error404" });
        else languageChanged = false;
      } else if (model.value.id) {
        // Set the site_type
        $ThemeManager.changeBySex(model.value.sex);

        resources.chatModel
          .storeAt({ id: model.value.id })
          .then(({ data }) => {
            loadingChat.value = false;
            model.value!.chat = data;
          })
          .catch(() => {
            console.log("Chat not available");
            loadingChat.value = true;
          });

          getPost(postID, model.value.id);
      }
    })
    .catch((err) => {
      loadingModel.value = false;
      errorModel.value = true;
    });
}

function videoHardAppendHandler(elem: any, handler: any) {
  videoRemoveHandler();
  moviePlaying = {
    ref: elem.id,
    handler,
  }
  videoPlayNext();
}

function videoAppendHandler(elem: any, handler: any) {
  videoRemoveHandler();
  moviePlaying = {
    ref: elem.id,
    handler,
  }
  videoPlayNext();
}

function videoRemoveHandler(elem: any = null) {
  if (!moviePlaying || (elem && elem.id !== moviePlaying.ref)) return;
  moviePlaying.handler("pause");
  moviePlaying = null;
}

function videoPlayNext() {
  if (!moviePlaying) return;
  moviePlaying.handler("mute", UserConfig.muted);
  moviePlaying.handler("play");
}

function videoMuteHandler() {
  if (!moviePlaying) return;

  UserConfig.toggleMuted();
  moviePlaying.handler("mute", UserConfig.muted);
}

onMounted(() => {
  const post = $route.params.post as string;
  const username = $route.params.username as string;

  loadPost(username, post);

  $EventBus.$on("languageChanged", () => {
    languageChanged = true;
    loadPost(username, post);
  });

  $EventBus.$on("openedUnlockPostWithTokens", (data: any) => {
    const [post, model] = data as [PostData, Model];

    unlockPostPost.value = post;
    unlockPostModel.value = model;
    openModalUnlockPost.value = true;
  });

  $EventBus.$on("unlockedPostWithTokens", (data: any) => {
    const [model, post] = data as [Model, PostData];
    loadingPost.value = true;
    getPost(post.post_id, model.id);
  });
});

if (process.server) {
  type _meta = {
    ogTitle: string;
    ogDescription: string;
    ogImage: string;
  };

  const { data } = await useAsyncData<_meta>(() => {
    return new Promise((resolve, reject) => {
      let { post: postID } = $route.params as { [key: string]: string };

      const fetchModelData = getFetchModelData(
        $route.params.username as string
      );

      resources.models
        .get(fetchModelData)
        .then(({ data }) => {
          const model: Model = data;

          if ((<any>model).length !== 0 && model.id) {
            resources.models
              .getPost({ postID, modelID: model.id })
              .then(({ data: post }) => {
                resolve({
                  ogTitle: model.name
                    .split(" ")
                    .map((s) => s[0].toUpperCase() + s.slice(1))
                    .join(" "),
                  ogDescription: post.description,
                  ogImage: post.public_thumbnail_image_path.large,
                });
              })
              .catch(reject);
          }
        })
        .catch(reject);
    });
  });

  useSeoMeta({
    ogTitle: () => data.value?.ogTitle || config.meta["og:title"],
    ogDescription: () =>
      data.value?.ogDescription || config.meta["og:description"],
    ogImage: () => data.value?.ogImage || config.meta["og:image"],
    ogUrl: () => $route.fullPath,
  });
}

onBeforeRouteUpdate((to, from, next) => {
  const post = to.params.post as string;
  const username = to.params.username as string;

  loadPost(username, post);
  next();
});

defineExpose({
  videoAppendHandler,
  videoHardAppendHandler,
  videoRemoveHandler,
  videoMuteHandler,
});
</script>

<style lang="scss">
@import "style.scss";
</style>
